/**
 * Consulta de conexión con el POS (polling), según el proveedor del método de pago.
 *
 * Todas las funciones resuelven (nunca rechazan) con `{status: 'OK', ...}` si el POS responde y
 * `{status: 'error'}` en cualquier otro caso, incluida una cancelación vía `signal`.
 */

/** Proveedores (`type_details` del método de pago) que soportan polling. */
export const POLLABLE_POS_TYPES = ['Getnet', 'MercadoPago', 'Transbank'];

/**
 * @param {Object} params
 * @param {string} params.env
 * @param {string} params.client
 * @param {string} params.instanceId
 * @param {string} params.storeId
 * @param {string} params.type - `type_details` del método de pago.
 * @param {AbortSignal} [params.signal]
 * @param {boolean} [params.automatic] - Poll que el tótem hace solo (pantalla inactiva). El backend
 *   no imprime nada en el terminal (Mercado Pago imprime un comprobante de prueba en los manuales).
 * @returns {Promise<{status: string, data?: Object}>}
 */
export async function pollPOS({ env, client, instanceId, storeId, type, signal, automatic = false }) {
  // Getnet y MercadoPago (POSAPI) se consultan vía backend, que resuelve el proveedor según el
  // terminal vinculado a la instancia. Para MercadoPago el backend solo verifica que ese terminal
  // exista en la cuenta; no consulta si el equipo está encendido.
  if (type === 'Getnet' || type === 'MercadoPago') return await backendPolling(env, client, instanceId, storeId, signal, automatic);
  if (type === 'Transbank') return await transbankPolling(signal);
  return { status: 'error' };
}

/**
 * Vincula un terminal (por su número de serie) a la instancia. Solo aplica a métodos de pago
 * `POSAPI`, cuyo POS se comunica a través del backend y no directamente con el equipo.
 *
 * @param {Object} params
 * @param {string} params.env
 * @param {string} params.client
 * @param {string} params.instanceId
 * @param {string} params.serialNumber
 * @returns {Promise<{ok: true} | {ok: false, message: string}>}
 */
export async function pairTerminal({ env, client, instanceId, serialNumber }) {
  try {
    const url = `https://pairterminal-x3herayjcq-uc.a.run.app?environment=${env}&client=${client}&instance_id=${instanceId}&query=${encodeURIComponent(serialNumber)}`;
    const response = await fetch(url, { method: "GET", headers: { "Content-Type": "application/json" } });
    if (!response.ok) {
      let message = 'Error de comunicación';
      try {
        message = (await response.json()).message || message;
      } catch {
        // La respuesta no es JSON; se usa el mensaje genérico.
      }
      return { ok: false, message };
    }
    return { ok: true };
  } catch (error) {
    console.error(error);
    return { ok: false, message: 'Error de comunicación' };
  }
}

async function backendPolling(env, client, instanceId, storeId, signal, automatic) {
  try {
    const url = `https://pollingterminal-x3herayjcq-uc.a.run.app?environment=${env}&client=${client}${automatic ? '&automatic=true' : ''}`;
    const body = { instance_id: instanceId, store_id: storeId };
    const response = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), signal: signal });
    if (!response.ok) {
      console.error(await response.text());
      return { status: 'error' };
    }
    return await response.json();
  } catch {
    return { status: 'error' };
  }
}

async function transbankPolling(signal) {
  try {
    const url = 'https://localhost:5001/poll';
    const response = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, signal: signal });
    if (!response.ok) return { status: 'error' };
    const data = await response.json();
    return { status: 'OK', data: data };
  } catch {
    return { status: 'error' };
  }
}
